/**
 * 初期パスワードの生成・検証ユーティリティ（クライアント共用）
 * ------------------------------------------------------------------
 * 「ユーザー単件登録・編集」PJ Ph.3 で新設。
 * 受講者管理画面の「パスワード初期化」列（RespondentMgrPage）と
 * ユーザー新規登録モーダル（UserFormModal mode='create'）で共用する。
 *
 * 規則は 2 種類あり、サーバー（rcdr_server/routes/users.js）と同一:
 *   1) 「自動生成」（generateInitialPassword）… 強い規則のまま変更なし
 *      - 12 文字ちょうど
 *      - 英大文字・英小文字・数字・記号を各 1 文字以上含む
 *      - 使用可能文字は英数字と記号（!@#$%^&*[]{}=?().,;:|~_）のみ
 *   2) 「直接入力」（validateInitialPassword）… 2026-09-17 に緩和
 *      - 半角の英数字・記号（ASCII 印字可能文字 0x21〜0x7E）のみ。空白は不可
 *      - 4 文字以上 72 文字以下。文字種（大文字・小文字・数字・記号）の必須要件はなし
 *
 * 直接入力の下限・上限・文字種制限の根拠:
 *   - 下限 4  … User モデルの password.minlength が 4（3 文字以下は保存エラーになる）
 *   - 上限 72 … bcrypt は先頭 72 バイトまでしかハッシュに使わない
 *   - 全角・空白は「管理者が意図した値と保存値のズレ」「ログイン不可」の原因になるため不可
 */

// 使用を許可する記号（自動生成で使用する集合）
export const INITIAL_PASSWORD_SYMBOLS = '!@#$%^&*[]{}=?().,;:|~_';

// 直接入力の文字数（サーバーと同一）
export const INITIAL_PASSWORD_MIN_LENGTH = 4;
export const INITIAL_PASSWORD_MAX_LENGTH = 72;

// 規則の説明文（フォームのヒント表示に使用）
export const INITIAL_PASSWORD_RULE_TEXT =
  '「自動生成」を推奨。直接入力は半角英数字・記号で 4〜72 文字（空白・全角文字は使用不可）';

// 初期パスワードの書式エラー（サーバーの INITIAL_PASSWORD_ERROR と同一文言）
export const INITIAL_PASSWORD_ERROR =
  '初期パスワードは半角英数字・記号で 4〜72 文字にしてください（空白・全角文字は使用できません）';

// 直接入力の検証用正規表現（ASCII 印字可能文字 0x21〜0x7E のみ・空白は含まない）
const INITIAL_PASSWORD_PATTERN = /^[\x21-\x7E]{4,72}$/;

const PASSWORD_LENGTH = 12;
const NUMERIC = '1234567890';
const UPPERCASE = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ';
const LOWERCASE = 'abcdefghijklmnopqrstuvwxyz';

// 指定文字列からランダムに 1 文字を取り出す
const pickChar = (source) =>
  source.charAt(Math.floor(Math.random() * source.length));

/**
 * 初期パスワードを自動生成する（12 文字）
 * 4 種類（数字・英大文字・英小文字・記号）を固定位置に 1 文字ずつ配置し、
 * 残りは全種混合から選ぶことで規則を必ず満たす。
 * @returns {string} 生成した 12 文字のパスワード
 */
export const generateInitialPassword = () => {
  const base = NUMERIC + UPPERCASE + LOWERCASE + INITIAL_PASSWORD_SYMBOLS;
  let password = '';
  for (let i = 0; i < PASSWORD_LENGTH; i++) {
    switch (i) {
      case 1:
        password += pickChar(NUMERIC);
        break;
      case 4:
        password += pickChar(UPPERCASE);
        break;
      case 7:
        password += pickChar(LOWERCASE);
        break;
      case 10:
        password += pickChar(INITIAL_PASSWORD_SYMBOLS);
        break;
      default:
        password += pickChar(base);
        break;
    }
  }
  return password;
};

/**
 * 初期パスワード（直接入力）の書式を検証する（サーバーと同一の規則）
 * 仕様: 半角の英数字・記号（ASCII 印字可能文字 0x21〜0x7E。空白は不可）のみで 4〜72 文字。
 *       文字種（大文字・小文字・数字・記号）の必須要件はない。
 * ※「自動生成」（generateInitialPassword）が作る 12 文字は常に本条件を満たす
 * @param {string} pw 検証対象のパスワード
 * @returns {boolean} 条件を満たす場合 true
 */
export const validateInitialPassword = (pw) =>
  typeof pw === 'string' && INITIAL_PASSWORD_PATTERN.test(pw);
